// English translations
export const en = {
  // Navigation
  navigation: {
    menu: {
      home: 'Home',
      ourStory: 'Our Story',
      services: 'Services',
      howItWorks: 'How It Works',
    },
    cta: {
      consultation: 'Book Free Consultation',
      languageSwitch: 'EN',
    },
  },
  
  // Hero Section
  hero: {
    title: {
      main: 'One-Stop U.S. Consulting & Lifestyle Concierge',
    },
    subtitle: {
      services: 'School · Legal Support · Housing · Business · Beverage Consulting · Elite Concierge',
    },
    cta: {
      consultation: 'Book Free 15-Min Consultation',
      viewServices: 'View All Services',
    },
  },
  
  // Footer
  footer: {
    brand: {
      title: 'Auren Stratus LLC',
      description: 'Professional U.S. Consulting & Lifestyle Management Services',
      tagline: 'Your trusted partner in America',
    },
    sections: {
      quickLinks: {
        title: 'Quick Links',
        subtitle: 'Navigate',
        services: 'Services',
        pricing: 'Pricing', 
        about: 'About',
        faq: 'FAQ',
        contact: 'Contact',
      },
      contact: {
        title: 'Contact',
        subtitle: 'Get in Touch',
        email: 'info@aurenstratus.com',
        location: 'United States',
      },
      wechat: {
        title: 'WeChat',
        subtitle: 'Connect with Us',
        scan: 'Scan to add on WeChat',
      },
    },
    legal: {
      copyright: '© 2025 Auren Stratus LLC. All rights reserved.',
      disclaimer: 'Legal Disclaimer: Auren Stratus LLC provides consulting and coordination services. We are not a law firm, real estate agency, or licensed healthcare provider. All specialized services are provided through our network of licensed professionals.',
    },
  },
  
  // Customer Personas Section
  customerPersonas: {
    section: {
      title: 'Who We Help',
      subtitle: 'Tailored Solutions for Every Need',
    },
    personas: {
      families: {
        title: 'Chinese Families in the U.S.',
        subtitle: 'Family Support Services',
        description: 'School consulting, bullying communication support, relocation assistance, comprehensive family support services',
      },
      entrepreneurs: {
        title: 'Chinese Entrepreneurs',
        subtitle: 'Business Development', 
        description: 'Business entry strategy, beverage product development, execution and operational support',
      },
      elite: {
        title: 'Elite & High Net Worth Individuals',
        subtitle: 'Premium Concierge',
        description: 'Private concierge services, exclusive lifestyle management, personalized premium support',
      },
    },
  },

  // Services Grid Section
  servicesGrid: {
    section: {
      title: 'Our Services',
      subtitle: 'Comprehensive Solutions for Your U.S. Journey',
    },
    services: {
      education: {
        title: 'Education Consulting',
        subtitle: 'Academic Success',
        description: 'School selection, enrollment support, and academic success strategies',
      },
      legal: {
        title: 'Legal Support Coordination',
        subtitle: 'Professional Network',
        description: 'Connect with trusted legal professionals for your specific needs',
      },
      realEstate: {
        title: 'Real Estate & Relocation',
        subtitle: 'Home Finding',
        description: 'Find your perfect home and seamless relocation assistance',
      },
      homeManagement: {
        title: 'Home Management',
        subtitle: 'Lifestyle Coordination',
        description: 'Comprehensive household management and lifestyle coordination',
      },
      spiritual: {
        title: 'Spiritual Wellness',
        subtitle: 'Holistic Support',
        description: 'Holistic wellness support and mental health resources',
      },
      business: {
        title: 'Business Consulting',
        subtitle: 'Strategic Planning',
        description: 'Strategic business planning and market entry guidance',
      },
      beverage: {
        title: 'Beverage Development',
        subtitle: 'Product Innovation',
        description: 'Product development and market strategy for beverage businesses',
      },
      elite: {
        title: 'Elite Concierge',
        subtitle: 'Premium Services',
        description: 'Exclusive personal assistance and premium lifestyle services',
      },
    },
    cta: {
      learnMore: 'Learn More',
    },
  },

  // Workflow Section
  workflow: {
    section: {
      title: 'How It Works',
      subtitle: 'Simple Steps to Get Started',
    },
    steps: {
      submit: {
        title: 'Submit Needs & Book',
        subtitle: 'Get Started',
        description: 'Fill out our form and schedule your free 15-minute consultation call',
      },
      consultant: {
        title: 'Consultant Advice',
        subtitle: 'Expert Guidance',
        description: 'Receive preliminary guidance and personalized recommendations from our expert',
      },
      payment: {
        title: 'Payment Details',
        subtitle: 'Next Steps',
        description: 'Get a follow-up email with service details and secure payment options',
      },
      support: {
        title: 'Ongoing Support',
        subtitle: 'Continuous Care',
        description: 'Continue with paid sessions or comprehensive project-based assistance',
      },
    },
  },

  // Testimonials Section
  testimonials: {
    section: {
      title: 'Client Testimonials',
      subtitle: 'What Our Clients Say',
      tagline: 'Trusted by dozens of Chinese families and entrepreneurs',
    },
    clients: {
      sarahLi: {
        name: 'Sarah Li',
        role: 'Business Owner',
        location: 'California',
        quote: 'Auren Stratus helped our entire family settle smoothly in the United States. From selecting the right school for our children to managing every aspect of family life, they provided professional guidance every step of the way.',
      },
      davidZhang: {
        name: 'David Zhang',
        role: 'Founder & CEO',
        location: 'New York',
        quote: 'Their beverage development consulting is highly professional. From product formulation to market strategy, they provided comprehensive support that helped my brand successfully enter the U.S. market.',
      },
      jenniferWang: {
        name: 'Jennifer Wang',
        role: 'Private Investor',
        location: 'Seattle',
        quote: 'The elite concierge service has made my life perfectly organized. Whether it\'s business arrangements or personal matters, there\'s a professional team supporting me behind the scenes. Truly worry-free living.',
      },
    },
  },

  // CTA Banner Section
  ctaBanner: {
    title: 'Get Expert Help in the U.S. Today',
    subtitle: 'Professional Support for Your American Journey',
    description: 'Whether you need education consulting, business support, or lifestyle management services, our professional team is ready to provide customized solutions tailored to your unique needs.',
    cta: 'Book Free 15-min Consultation',
  },

  // Our Story Page
  ourStory: {
    hero: {
      title: 'Our Story',
      description: 'We started Auren Stratus LLC because moving, learning, and building a life in the U.S. shouldn\'t be so overwhelming.',
    },
    inspiration: {
      team: 'Many of us on the team are immigrants, parents, and founders ourselves. We know the paperwork, the uncertainty, and the emotional stress of living in a new country.',
      mission: 'We turned our personal experiences into a mission — to make your U.S. journey confident, informed, and supported.',
    },
    problem: {
      title: 'The Problem We Solve',
      tagline: 'You don\'t have to do this alone.',
      challenges: {
        families: {
          title: 'Families',
          description: 'Struggle with school choices and communication',
        },
        founders: {
          title: 'Founders',
          description: 'Face complexity entering the U.S. market',
        },
        highNetWorth: {
          title: 'High-Net-Worth',
          description: 'Need discreet and reliable support',
        },
      },
    },
    differences: {
      title: 'What Makes Us Different',
      items: {
        bilingual: {
          title: 'Bilingual Team',
          description: 'U.S. and Chinese background with deep cultural understanding',
        },
        execution: {
          title: 'Real Execution',
          description: 'Not just advice — we help you get things done',
        },
        tailored: {
          title: 'Tailored Guidance',
          description: 'Custom support designed for your real needs',
        },
        relationship: {
          title: 'Relationship-Driven',
          description: 'Confidential, caring, and committed to your success',
        },
      },
    },
    team: {
      title: 'Meet the Team',
      subtitle: 'Experienced professionals dedicated to your success',
      members: {
        education: {
          role: 'Education Strategy Consultant',
          expertise: '10+ years supporting families through U.S. school transitions',
        },
        legal: {
          role: 'Legal Coordination Specialist',
          expertise: 'Expert in immigration and cross-border legal support',
        },
        business: {
          role: 'Business Development Advisor',
          expertise: 'Helped 50+ Chinese entrepreneurs enter U.S. markets',
        },
        concierge: {
          role: 'Concierge Services Director',
          expertise: 'Premium lifestyle management for high-net-worth clients',
        },
      },
    },
    promise: {
      quote: 'We support your life, dreams, and milestones — with the care we wish we had when we first arrived.',
    },
    cta: {
      title: 'Let\'s walk this journey together.',
      button: 'Book Free 15-min Consultation',
    },
  },

  // Services Overview Page
  servicesOverview: {
    hero: {
      title: 'Our Services',
      description: 'One-stop consulting and settlement support for Chinese families and founders in the U.S.',
    },
    services: {
      schoolFamily: {
        title: 'School & Family Support',
        description: 'Navigate the U.S. education system with confidence. From school selection to parent-teacher engagement, we help your family thrive academically and socially.',
        pricing: '$300–$3,500',
      },
      legal: {
        title: 'Legal Support Coordination', 
        description: 'Connect with trusted legal professionals for visa, immigration, and compliance matters. We coordinate the process so you can focus on what matters most.',
        pricing: '$80–$150/hr',
      },
      realEstate: {
        title: 'Real Estate & Relocation',
        description: 'Find your ideal home in the right neighborhood. We provide end-to-end support from property search to move-in coordination and local orientation.',
        pricing: '$300–$2,000',
      },
      homeManagement: {
        title: 'Home Management',
        description: 'Simplify your daily life with trusted home service coordination. From contractors to housekeeping, we ensure quality and peace of mind.',
        pricing: '$199–$799/mo or $99–$299/visit',
      },
      spiritual: {
        title: 'Spiritual Wellness',
        description: 'Find community and spiritual support as you settle into American life. We connect you with faith-based resources and cultural programs.',
        pricing: '$250–$2,500',
      },
      business: {
        title: 'Business Consulting',
        description: 'Launch and grow your U.S. business with strategic guidance. From market entry to operations, we help you navigate the American business landscape.',
        pricing: '$300–$1,500+',
      },
      beverage: {
        title: 'Beverage Development Consulting',
        description: 'Bring your beverage concept to market with expert guidance on formulation, compliance, branding, and distribution in the U.S. market.',
        pricing: 'From $3,000+',
      },
      elite: {
        title: 'Elite Concierge Services',
        description: 'Experience white-glove personal assistance for every aspect of your life. From travel planning to exclusive event access, we handle the details.',
        pricing: '$12,000–$75,000/year',
      },
    },
    pricing: {
      label: 'Pricing: ',
    },
    cta: {
      learnMore: 'Learn More',
      title: 'Not sure which service fits your situation?',
      description: 'Start with a free 15-min consultation. We\'ll listen to your needs and recommend the best path forward.',
      button: 'Book Free 15-min Call',
    },
  },

  // How It Works Page
  howItWorks: {
    hero: {
      title: 'How It Works',
      description: 'We make your U.S. journey simple. One team to help you navigate school, life, business, and opportunity — all starting with a free 15-minute call.',
    },
    process: {
      title: 'Our 4-Step Process',
      subtitle: 'Simple → Transparent → Bilingual → Real execution, not just advice.',
      steps: {
        booking: {
          title: 'Book Your Free Call',
          description: 'You choose a service category and submit a short description of your needs. We meet on a 15-minute call to understand your goals.',
        },
        recommendation: {
          title: 'Receive a Personalized Recommendation',
          description: 'Based on your case, we assess what you actually need and what success looks like — including clear scope and pricing options.',
        },
        payment: {
          title: 'Continue with Paid Services',
          description: 'If you decide to move forward, we send a secure payment link by email with the recommended consulting plan. No commitment required after the free call.',
        },
        execution: {
          title: 'Execution & Ongoing Support',
          description: 'We schedule deeper sessions or concierge-style action to help you implement — whether it\'s school support, relocation, business strategy, beverage development, or private concierge needs.',
        },
      },
    },
    expectations: {
      title: 'What You Can Expect',
      items: {
        guidance: {
          title: 'Clear guidance, no upsell pressure',
          description: 'We focus on what you need, not what we want to sell.',
        },
        pricing: {
          title: 'Transparent pricing based on scope',
          description: 'You\'ll know exactly what you\'re paying for before committing.',
        },
        bilingual: {
          title: 'Bilingual cultural alignment',
          description: 'We understand both U.S. and Chinese business cultures deeply.',
        },
        confidential: {
          title: 'Confidential, high-touch execution support',
          description: 'Your privacy is protected with personalized, hands-on assistance.',
        },
      },
    },
    faq: {
      title: 'Frequently Asked Questions',
      questions: {
        freeCall: {
          question: 'Is the free call truly free?',
          answer: 'Yes. The 15-minute consultation is completely free. There is no commitment and no payment required unless you decide to continue with paid services afterward.',
        },
        oneTime: {
          question: 'If I only need help once, can I stop there?',
          answer: 'Yes. You can choose a single paid session after the free call. There are no long-term contracts — you stay in full control of what you book.',
        },
        legal: {
          question: 'Do you provide legal advice?',
          answer: 'No. We do not provide legal advice, legal representation, or attorney services. We can help you with: organizing case-related information, guiding communication logistics, and coordinating with licensed attorneys when needed. (Important: final legal judgment or legal direction must come from a licensed attorney.)',
        },
        cost: {
          question: 'How will I know the final cost?',
          answer: 'After the free call, we provide a clear service recommendation with: scope tailored to your situation, transparent pricing (hourly, package, or project-based), and a secure payment link sent to your email. You approve before anything continues — no surprises.',
        },
      },
    },
    finalCta: {
      title: 'Ready to get real help in the U.S.?',
      button: 'Book Free 15-min Call',
    },
  },

  // Individual Service Pages
  services: {
    // School & Family Support Page
    schoolFamily: {
      hero: {
        title: 'School & Family Support',
        description: 'Expert guidance for school choices, communication, and family success in the U.S.',
        subtitle: 'Serving Chinese families with full cultural understanding.',
        cta: 'Book Free 15-min Consultation',
      },
      whatWeHelp: {
        title: 'What We Help With',
        subtitle: 'Clear, calming support aligned with your family\'s real needs.',
        services: {
          enrollment: {
            title: 'School choice & enrollment guidance',
            description: 'Private school or public school strategy',
          },
          communication: {
            title: 'Communication with teachers & school administrators',
            description: 'Navigate conversations with confidence',
          },
          bullying: {
            title: 'Support for bullying / behavior concerns',
            description: 'Advocate for your child effectively',
          },
          iep: {
            title: 'IEP / 504 plan coordination',
            description: 'Non-legal guidance only',
          },
          transition: {
            title: 'Family transition support after relocation',
            description: 'Settle into your new community',
          },
          coaching: {
            title: 'Parent coaching for navigating U.S. school system',
            description: 'Build confidence in your parenting journey',
          },
        },
      },
      howItWorks: {
        title: 'How It Works',
        steps: {
          call: {
            title: 'Free 15-min Call',
            description: 'Tell us your child\'s situation and goals.',
          },
          plan: {
            title: 'Personalized Plan',
            description: 'We recommend the right school path and support approach.',
          },
          sessions: {
            title: 'Paid Follow-up Sessions',
            description: 'Continue with communication, paperwork, coaching, or planning.',
          },
          results: {
            title: 'Progress & Results',
            description: 'We stay with you through important school moments.',
          },
        },
      },
      pricing: {
        title: 'Simple & Transparent Pricing',
        description: 'Pricing starts at $50 per 15-minute session.',
        details: 'You can select the number of sessions you need — your total will update automatically. No commitment beyond what you book.',
        followUp: 'If you later decide to continue, we will provide a tailored proposal with a clear scope and quote — based on your unique goals.',
        cta: 'Book Your Session',
      },
      whyChoose: {
        title: 'Why Families Choose Us',
        highlights: {
          bilingual: 'Bilingual specialists in U.S. education and family support',
          cultural: 'Understanding of Chinese culture and expectations',
          practical: 'Practical guidance that leads to real results',
          confidential: 'Confidential, friendly, and supportive partnership',
        },
      },
      faq: {
        title: 'Frequently Asked Questions',
        questions: {
          guarantee: {
            question: 'Can you guarantee school admission?',
            answer: 'We cannot guarantee results, but we maximize your opportunities with preparation and guidance.',
          },
          legal: {
            question: 'Do you provide legal advice?',
            answer: 'No. We help coordinate IEP/504 and school procedures, but legal opinions must come from attorneys.',
          },
          online: {
            question: 'Can we do everything online?',
            answer: 'Yes. We support families across the U.S. and internationally.',
          },
          preparation: {
            question: 'How should I prepare for the free call?',
            answer: 'Bring your child\'s age, grade level, current challenges, and school goals.',
          },
        },
      },
      finalCta: {
        title: 'Let\'s help your child succeed in the U.S.',
        button: 'Book Free 15-min Consultation',
      },
    },

    // Business Consulting Page
    businessConsulting: {
      hero: {
        title: 'Business Consulting & U.S. Market Entry',
        description: 'From concept to execution — helping Chinese founders succeed in the U.S.',
        cta: 'Book Free 15-min Consultation',
      },
      whatWeHelp: {
        title: 'What We Help With',
        subtitle: 'Strategic guidance from concept through execution.',
        services: {
          strategy: {
            title: 'Business strategy for U.S. market',
            description: 'Navigate the American business landscape',
          },
          planning: {
            title: 'Action planning and operational guidance',
            description: 'Turn strategy into executable steps',
          },
          vendor: {
            title: 'Vendor coordination and execution follow-through',
            description: 'Manage relationships and deliverables',
          },
          networking: {
            title: 'Community and local resource networking',
            description: 'Connect with the right people and resources',
          },
          setup: {
            title: 'Company setup support (non-legal)',
            description: 'Guidance through formation and operations',
          },
        },
      },
      howItWorks: {
        title: 'How It Works',
        steps: {
          call: {
            title: 'Free 15-min Call',
            description: 'Share your business vision and current stage.',
          },
          plan: {
            title: 'Personalized Plan',
            description: 'We recommend the right strategy and approach.',
          },
          sessions: {
            title: 'Paid Follow-up Sessions',
            description: 'Continue with consulting, planning, and coordination.',
          },
          results: {
            title: 'Progress & Results',
            description: 'Execute and grow with ongoing support.',
          },
        },
      },
      pricing: {
        title: 'Simple & Transparent Pricing',
        description: 'Pricing starts at $50 per 15-minute session.',
        details: 'You can select the number of sessions you need — your total will update automatically. No commitment beyond what you book.',
        followUp: 'If you later decide to continue, we will provide a tailored proposal with a clear scope and quote — based on your unique goals.',
        cta: 'Book Your Session',
      },
      whyChoose: {
        title: 'Why Choose Us',
        highlights: {
          context: 'Understand both U.S. and China business context',
          execution: 'Execution-focused support',
          tailored: 'Tailored guidance to your real stage',
          discreet: 'Discreet handling of sensitive ventures',
        },
      },
      faq: {
        title: 'Frequently Asked Questions',
        questions: {
          legal: {
            question: 'Do you provide legal or accounting advice?',
            answer: 'We are not legal or accounting advisors. We provide business strategy and operational guidance, and can connect you with licensed professionals for legal and financial matters.',
          },
          confidential: {
            question: 'Is everything confidential?',
            answer: 'Yes. Full confidentiality is maintained at all times. We can also sign NDAs when appropriate.',
          },
          remote: {
            question: 'Can you work with me remotely?',
            answer: 'Absolutely. Remote-first support is available, with in-person meetings when beneficial.',
          },
          industries: {
            question: 'What industries do you work with?',
            answer: 'We work across various industries, with particular strength in market entry, product development, and service-based businesses.',
          },
        },
      },
      finalCta: {
        title: 'Build your business with a trusted partner.',
        button: 'Book Free 15-min Consultation',
      },
    },

    // Legal Support Coordination Page
    legalSupport: {
      hero: {
        title: 'Legal Support Coordination',
        description: 'Connect with trusted legal professionals for your specific immigration and legal needs.',
        subtitle: 'Professional coordination for visa, immigration, and compliance matters.',
        cta: 'Book Free 15-min Consultation',
      },
      whatWeHelp: {
        title: 'What We Help With',
        subtitle: 'Professional legal coordination and guidance for your U.S. journey.',
        services: {
          visa: {
            title: 'Visa and immigration coordination',
            description: 'Connect with qualified immigration attorneys',
          },
          compliance: {
            title: 'Legal compliance guidance',
            description: 'Navigate U.S. legal requirements with confidence',
          },
          documentation: {
            title: 'Document preparation support',
            description: 'Organize and prepare legal documentation',
          },
          consultation: {
            title: 'Legal consultation coordination',
            description: 'Schedule and coordinate with legal professionals',
          },
          representation: {
            title: 'Legal representation coordination',
            description: 'Connect with specialized attorneys for your case',
          },
          ongoing: {
            title: 'Ongoing legal matter support',
            description: 'Continuous coordination throughout legal processes',
          },
        },
      },
      howItWorks: {
        title: 'How It Works',
        steps: {
          call: {
            title: 'Free 15-min Call',
            description: 'Share your legal needs and current situation.',
          },
          plan: {
            title: 'Professional Matching',
            description: 'We connect you with qualified legal professionals.',
          },
          sessions: {
            title: 'Coordination Support',
            description: 'We help coordinate communications and processes.',
          },
          results: {
            title: 'Legal Progress',
            description: 'Achieve your legal objectives with professional support.',
          },
        },
      },
      pricing: {
        title: 'Simple & Transparent Pricing',
        description: 'Legal coordination starts at $80-$150 per hour.',
        details: 'We provide coordination services only - legal fees are separate and paid directly to attorneys.',
        followUp: 'We will connect you with qualified legal professionals and coordinate the entire process for your peace of mind.',
        cta: 'Get Legal Coordination',
      },
      whyChoose: {
        title: 'Why Choose Our Legal Coordination',
        highlights: {
          network: 'Trusted network of qualified legal professionals',
          bilingual: 'Bilingual support throughout legal processes',
          coordination: 'Full coordination and communication management',
          peace: 'Peace of mind with professional oversight',
        },
      },
      faq: {
        title: 'Frequently Asked Questions',
        questions: {
          legal: {
            question: 'Do you provide legal advice?',
            answer: 'No. We provide coordination services only. All legal advice and representation comes from licensed attorneys in our network.',
          },
          fees: {
            question: 'How do legal fees work?',
            answer: 'You pay coordination fees to us and legal fees directly to the attorney. We provide transparent pricing for both.',
          },
          selection: {
            question: 'How do you select attorneys?',
            answer: 'We work with pre-vetted, qualified attorneys who specialize in immigration and business law for Chinese clients.',
          },
          support: {
            question: 'What support do you provide during the process?',
            answer: 'We coordinate all communications, help prepare documentation, and ensure smooth processes throughout your legal matter.',
          },
        },
      },
      finalCta: {
        title: 'Get professional legal coordination today.',
        button: 'Book Free 15-min Consultation',
      },
    },

    // Real Estate & Relocation Page
    realEstate: {
      hero: {
        title: 'Real Estate & Relocation Support',
        description: 'Find your perfect home and seamless relocation assistance in the U.S.',
        subtitle: 'Complete home-finding and relocation support for your family.',
        cta: 'Book Free 15-min Consultation',
      },
      whatWeHelp: {
        title: 'What We Help With',
        subtitle: 'End-to-end support for your housing and relocation needs.',
        services: {
          search: {
            title: 'Home search and neighborhood selection',
            description: 'Find the perfect location for your family',
          },
          coordination: {
            title: 'Real estate agent coordination',
            description: 'Connect with trusted local real estate professionals',
          },
          inspection: {
            title: 'Home inspection and evaluation support',
            description: 'Navigate the home buying process with confidence',
          },
          moving: {
            title: 'Moving and logistics coordination',
            description: 'Seamless relocation planning and execution',
          },
          schools: {
            title: 'School district research and enrollment',
            description: 'Ensure your children have access to quality education',
          },
          community: {
            title: 'Community integration support',
            description: 'Connect with local resources and communities',
          },
        },
      },
      howItWorks: {
        title: 'How It Works',
        steps: {
          call: {
            title: 'Free 15-min Call',
            description: 'Discuss your housing needs and relocation timeline.',
          },
          plan: {
            title: 'Customized Search Plan',
            description: 'We create a targeted home search strategy for you.',
          },
          sessions: {
            title: 'House Hunting Support',
            description: 'Ongoing support throughout the home search process.',
          },
          results: {
            title: 'Move-in Ready',
            description: 'Complete relocation support until you\'re settled.',
          },
        },
      },
      pricing: {
        title: 'Simple & Transparent Pricing',
        description: 'Relocation support from $300-$2,000 based on scope.',
        details: 'Pricing varies based on location, timeline, and level of support needed.',
        followUp: 'We provide comprehensive support from initial search through successful relocation.',
        cta: 'Start Your Home Search',
      },
      whyChoose: {
        title: 'Why Choose Our Relocation Support',
        highlights: {
          local: 'Local expertise in family-friendly communities',
          schools: 'Deep knowledge of school districts and education options',
          cultural: 'Understanding of Chinese family housing preferences',
          support: 'Full-service support from search to settlement',
        },
      },
      faq: {
        title: 'Frequently Asked Questions',
        questions: {
          agent: {
            question: 'Do you work with specific real estate agents?',
            answer: 'We work with a trusted network of agents who understand the needs of Chinese families relocating to the U.S.',
          },
          areas: {
            question: 'Which areas do you cover?',
            answer: 'We provide support nationwide, with particular expertise in major metropolitan areas with strong school districts.',
          },
          timeline: {
            question: 'How long does the process take?',
            answer: 'Timeline varies based on market conditions and your requirements, typically 2-6 months for a complete relocation.',
          },
          remote: {
            question: 'Can you help if I\'m still in China?',
            answer: 'Yes, we provide full remote support including virtual tours, market research, and relocation planning.',
          },
        },
      },
      finalCta: {
        title: 'Find your perfect home in America.',
        button: 'Book Free 15-min Consultation',
      },
    },

    // Home Management Page
    homeManagement: {
      hero: {
        title: 'Home Management Services',
        description: 'Comprehensive household management and lifestyle coordination for busy families.',
        subtitle: 'Trusted home service coordination and quality assurance.',
        cta: 'Book Free 15-min Consultation',
      },
      whatWeHelp: {
        title: 'What We Help With',
        subtitle: 'Professional coordination of all your household needs.',
        services: {
          maintenance: {
            title: 'Home maintenance and repair coordination',
            description: 'Trusted contractors and quality service management',
          },
          cleaning: {
            title: 'Housekeeping and cleaning services',
            description: 'Regular and deep cleaning service coordination',
          },
          landscaping: {
            title: 'Landscaping and outdoor maintenance',
            description: 'Beautiful and well-maintained outdoor spaces',
          },
          security: {
            title: 'Home security system management',
            description: 'Installation and monitoring coordination',
          },
          utilities: {
            title: 'Utility setup and management',
            description: 'Seamless utility coordination and bill management',
          },
          seasonal: {
            title: 'Seasonal home preparation',
            description: 'Winterization, summer prep, and seasonal maintenance',
          },
        },
      },
      howItWorks: {
        title: 'How It Works',
        steps: {
          call: {
            title: 'Free 15-min Call',
            description: 'Discuss your home management needs and preferences.',
          },
          plan: {
            title: 'Service Plan Creation',
            description: 'We design a customized home management plan.',
          },
          sessions: {
            title: 'Ongoing Coordination',
            description: 'Regular service coordination and quality monitoring.',
          },
          results: {
            title: 'Worry-free Living',
            description: 'Enjoy your home while we handle the management.',
          },
        },
      },
      pricing: {
        title: 'Simple & Transparent Pricing',
        description: 'Home management from $199-$799/month or $99-$299 per visit.',
        details: 'Pricing based on frequency of service and scope of home management needs.',
        followUp: 'Monthly plans offer the best value for ongoing home management coordination.',
        cta: 'Start Home Management',
      },
      whyChoose: {
        title: 'Why Choose Our Home Management',
        highlights: {
          vetted: 'Pre-screened and trusted service professionals',
          quality: 'Quality assurance and service monitoring',
          bilingual: 'Bilingual coordination and communication',
          peace: 'Complete peace of mind for busy families',
        },
      },
      faq: {
        title: 'Frequently Asked Questions',
        questions: {
          services: {
            question: 'What services do you coordinate?',
            answer: 'We coordinate all household services including cleaning, maintenance, landscaping, security, and utilities management.',
          },
          emergency: {
            question: 'Do you handle emergency repairs?',
            answer: 'Yes, we provide 24/7 emergency coordination for urgent home repairs and issues.',
          },
          staff: {
            question: 'How do you select service providers?',
            answer: 'All service providers are pre-screened, insured, and have excellent references from other clients.',
          },
          flexibility: {
            question: 'Can I adjust services as needed?',
            answer: 'Absolutely. You can adjust your service plan anytime based on changing needs and preferences.',
          },
        },
      },
      finalCta: {
        title: 'Simplify your life with professional home management.',
        button: 'Book Free 15-min Consultation',
      },
    },

    // Spiritual Wellness Page
    spiritual: {
      hero: {
        title: 'Spiritual Wellness Support',
        description: 'Find community, faith-based support, and spiritual wellness resources as you settle in America.',
        subtitle: 'Holistic wellness and spiritual community connections.',
        cta: 'Book Free 15-min Consultation',
      },
      whatWeHelp: {
        title: 'What We Help With',
        subtitle: 'Comprehensive spiritual and wellness support for your journey.',
        services: {
          community: {
            title: 'Faith community connections',
            description: 'Find welcoming spiritual communities and congregations',
          },
          counseling: {
            title: 'Spiritual counseling coordination',
            description: 'Connect with qualified spiritual counselors and advisors',
          },
          wellness: {
            title: 'Mental health and wellness resources',
            description: 'Holistic approach to mental and emotional wellbeing',
          },
          cultural: {
            title: 'Cultural and spiritual adaptation support',
            description: 'Navigate cultural differences while maintaining your faith',
          },
          programs: {
            title: 'Religious education and programs',
            description: 'Find programs for children and family spiritual growth',
          },
          ceremonies: {
            title: 'Religious ceremony coordination',
            description: 'Support for important spiritual milestones and ceremonies',
          },
        },
      },
      howItWorks: {
        title: 'How It Works',
        steps: {
          call: {
            title: 'Free 15-min Call',
            description: 'Share your spiritual and wellness needs with us.',
          },
          plan: {
            title: 'Community Matching',
            description: 'We help you find the right spiritual community and resources.',
          },
          sessions: {
            title: 'Ongoing Support',
            description: 'Continued guidance through your spiritual journey.',
          },
          results: {
            title: 'Spiritual Wellness',
            description: 'Achieve spiritual balance and community connection.',
          },
        },
      },
      pricing: {
        title: 'Simple & Transparent Pricing',
        description: 'Spiritual wellness support from $250-$2,500 based on needs.',
        details: 'Pricing varies based on level of support and specific spiritual wellness requirements.',
        followUp: 'We provide sensitive and respectful support for your spiritual journey in America.',
        cta: 'Begin Your Spiritual Journey',
      },
      whyChoose: {
        title: 'Why Choose Our Spiritual Support',
        highlights: {
          respectful: 'Respectful approach to all faith traditions and beliefs',
          community: 'Strong network of spiritual communities and leaders',
          holistic: 'Holistic approach to spiritual and mental wellness',
          cultural: 'Understanding of cultural and spiritual adaptation challenges',
        },
      },
      faq: {
        title: 'Frequently Asked Questions',
        questions: {
          denominations: {
            question: 'Do you work with specific religions?',
            answer: 'We support people of all faith backgrounds including Christianity, Buddhism, Taoism, and secular spiritual practices.',
          },
          counseling: {
            question: 'Do you provide spiritual counseling?',
            answer: 'We coordinate with qualified spiritual counselors and chaplains, but do not provide counseling services directly.',
          },
          children: {
            question: 'Do you help with children\'s spiritual education?',
            answer: 'Yes, we help families find appropriate religious education programs and youth groups for their children.',
          },
          confidential: {
            question: 'Is spiritual support confidential?',
            answer: 'Absolutely. All spiritual and wellness support is provided with complete confidentiality and respect.',
          },
        },
      },
      finalCta: {
        title: 'Find spiritual peace and community in America.',
        button: 'Book Free 15-min Consultation',
      },
    },

    // Beverage Development Consulting Page
    beverage: {
      hero: {
        title: 'Beverage Development Consulting',
        description: 'Bring your beverage concept to market with expert guidance on formulation, compliance, and distribution.',
        subtitle: 'Complete beverage development from concept to market launch.',
        cta: 'Book Free 15-min Consultation',
      },
      whatWeHelp: {
        title: 'What We Help With',
        subtitle: 'End-to-end beverage development and market entry support.',
        services: {
          formulation: {
            title: 'Product formulation and recipe development',
            description: 'Create market-ready beverage formulations',
          },
          compliance: {
            title: 'FDA compliance and regulatory guidance',
            description: 'Navigate U.S. food and beverage regulations',
          },
          branding: {
            title: 'Brand development and packaging design',
            description: 'Create compelling brand identity and packaging',
          },
          manufacturing: {
            title: 'Manufacturing partner coordination',
            description: 'Connect with qualified beverage manufacturers',
          },
          distribution: {
            title: 'Distribution strategy and channel development',
            description: 'Build effective distribution networks',
          },
          launch: {
            title: 'Market launch and go-to-market strategy',
            description: 'Successfully introduce your beverage to market',
          },
        },
      },
      howItWorks: {
        title: 'How It Works',
        steps: {
          call: {
            title: 'Free 15-min Call',
            description: 'Share your beverage concept and business goals.',
          },
          plan: {
            title: 'Development Roadmap',
            description: 'We create a comprehensive development and launch plan.',
          },
          sessions: {
            title: 'Expert Guidance',
            description: 'Ongoing consultation throughout development process.',
          },
          results: {
            title: 'Market Launch',
            description: 'Successfully bring your beverage to market.',
          },
        },
      },
      pricing: {
        title: 'Simple & Transparent Pricing',
        description: 'Beverage development consulting from $3,000+ based on project scope.',
        details: 'Comprehensive packages available for full product development from concept to launch.',
        followUp: 'We provide expert guidance through every stage of beverage development and market entry.',
        cta: 'Start Your Beverage Project',
      },
      whyChoose: {
        title: 'Why Choose Our Beverage Expertise',
        highlights: {
          experience: 'Extensive experience in U.S. beverage market',
          network: 'Strong network of manufacturers and distributors',
          compliance: 'Deep knowledge of FDA and industry regulations',
          success: 'Proven track record of successful beverage launches',
        },
      },
      faq: {
        title: 'Frequently Asked Questions',
        questions: {
          timeline: {
            question: 'How long does beverage development take?',
            answer: 'Timeline varies by complexity, typically 6-18 months from concept to market launch depending on formulation and regulatory requirements.',
          },
          minimum: {
            question: 'What are minimum production quantities?',
            answer: 'Minimum quantities depend on the manufacturer and product type, typically starting at 500-1000 cases for initial production runs.',
          },
          costs: {
            question: 'What are the total costs involved?',
            answer: 'Total costs include consulting, formulation, regulatory, manufacturing setup, and initial production. We provide detailed cost breakdowns during planning.',
          },
          international: {
            question: 'Can you help with international beverage concepts?',
            answer: 'Yes, we specialize in helping Chinese entrepreneurs bring traditional and innovative beverage concepts to the U.S. market.',
          },
        },
      },
      finalCta: {
        title: 'Turn your beverage dream into reality.',
        button: 'Book Free 15-min Consultation',
      },
    },

    // Elite Concierge Services Page
    elite: {
      hero: {
        title: 'Elite Concierge Services',
        description: 'White-glove personal assistance and premium lifestyle management for discerning clients.',
        subtitle: 'Exclusive concierge services for high-net-worth individuals and families.',
        cta: 'Book Free 15-min Consultation',
      },
      whatWeHelp: {
        title: 'What We Help With',
        subtitle: 'Comprehensive lifestyle management and personal assistance services.',
        services: {
          travel: {
            title: 'Luxury travel planning and coordination',
            description: 'Exclusive travel experiences and seamless logistics',
          },
          events: {
            title: 'Private event planning and access',
            description: 'Access to exclusive events and custom event planning',
          },
          dining: {
            title: 'Fine dining reservations and experiences',
            description: 'Access to premier restaurants and culinary experiences',
          },
          personal: {
            title: 'Personal shopping and lifestyle services',
            description: 'Curated shopping experiences and personal styling',
          },
          business: {
            title: 'Executive assistant and business support',
            description: 'High-level executive support and business coordination',
          },
          family: {
            title: 'Family and household management',
            description: 'Complete family lifestyle coordination and support',
          },
        },
      },
      howItWorks: {
        title: 'How It Works',
        steps: {
          call: {
            title: 'Confidential Consultation',
            description: 'Discuss your lifestyle needs and service expectations.',
          },
          plan: {
            title: 'Personalized Service Plan',
            description: 'We design a bespoke concierge service package.',
          },
          sessions: {
            title: 'Dedicated Support',
            description: 'Your dedicated concierge team provides ongoing support.',
          },
          results: {
            title: 'Elevated Lifestyle',
            description: 'Experience seamless luxury lifestyle management.',
          },
        },
      },
      pricing: {
        title: 'Premium Service Pricing',
        description: 'Elite concierge services from $12,000-$75,000 annually based on service level.',
        details: 'Customizable service packages ranging from part-time assistance to full-time dedicated concierge teams.',
        followUp: 'All services are provided with the utmost discretion and attention to detail.',
        cta: 'Explore Elite Services',
      },
      whyChoose: {
        title: 'Why Choose Our Elite Concierge',
        highlights: {
          exclusive: 'Access to exclusive venues, events, and experiences',
          discretion: 'Absolute discretion and confidentiality',
          personalized: 'Highly personalized and attentive service',
          network: 'Extensive network of luxury service providers',
        },
      },
      faq: {
        title: 'Frequently Asked Questions',
        questions: {
          availability: {
            question: 'What are your availability hours?',
            answer: '24/7 availability for urgent requests, with dedicated hours based on your service level and preferences.',
          },
          services: {
            question: 'What services are included?',
            answer: 'Services are fully customizable based on your needs, from travel and dining to business support and family coordination.',
          },
          privacy: {
            question: 'How do you ensure privacy?',
            answer: 'We maintain the highest standards of confidentiality with comprehensive NDAs and strict privacy protocols.',
          },
          international: {
            question: 'Do you provide international support?',
            answer: 'Yes, we provide seamless support whether you\'re in the U.S. or traveling internationally.',
          },
        },
      },
      finalCta: {
        title: 'Experience the finest in lifestyle management.',
        button: 'Book Private Consultation',
      },
    },
  },

  // Booking Page
  booking: {
    header: {
      title: 'Book Your Free 15-min Consultation',
      subtitle: 'Get clarity and guidance — no obligation to continue.',
      paidSessionTitle: 'Book Your Session',
      paidSessionSubtitle: 'Select your session length and complete payment.',
    },
    services: {
      school: 'School & Family Support',
      legal: 'Legal Support Coordination',
      realestate: 'Real Estate & Relocation',
      homemanagement: 'Home Management',
      spiritual: 'Spiritual Wellness',
      business: 'Business Consulting',
      beverage: 'Beverage Development',
      concierge: 'Elite Concierge',
      unsure: 'Not sure yet',
    },
    form: {
      serviceSelection: {
        title: 'Choose Your Topic',
        subtitle: 'Choose the closest area — we\'ll guide you from there.',
      },
      personalInfo: {
        title: 'Tell Us About Your Needs',
        firstName: {
          label: 'First Name',
          placeholder: 'Enter your first name',
        },
        meetingMethod: {
          label: 'How would you like to meet?',
          zoom: 'Zoom',
          phone: 'Phone',
          wechat: 'WeChat',
        },
        email: {
          label: 'Email',
          placeholder: 'your@email.com',
        },
        phone: {
          label: 'Phone Number',
          placeholder: '123-456-7890',
        },
        wechatId: {
          label: 'WeChat ID',
          placeholder: 'your-wechat-id',
        },
        message: {
          label: 'Message',
          placeholder: 'What would you like to discuss?',
        },
        fileUpload: {
          label: 'Document Upload (Optional)',
          placeholder: 'Click to upload a file',
        },
        privacyNote: 'Your information is kept private and is only used to schedule your call.',
      },
    },
    timeSelection: {
      title: 'Select Your Time',
      subtitle: 'Choose a 15-minute slot that works for you.',
      stepLabel: 'Step 2 of 2',
      paidTitle: 'Select Your Time Slots',
      paidSubtitle: 'Choose one or more time slots. Each slot is 30 minutes at $150.',
      stepOf: 'Step {current} of {total}',
      calendar: {
        timezoneNote: 'Times shown in your local timezone (Auto-detected)',
        editTimezone: 'Edit timezone',
      },
      timeSlots: {
        title: 'Available 15-minute slots',
      },
      appointmentSummary: {
        title: 'Your Appointment',
        topic: 'Topic',
        name: 'Name',
        contact: 'Contact',
        email: 'Email',
        phone: 'Phone',
        wechat: 'WeChat',
        scheduledFor: 'Scheduled For',
        meetingMethod: 'Meeting Method',
        via: 'via',
        orderSummary: 'Order Summary',
        service: 'Service',
        sessions: 'Sessions',
        total: 'Total',
        scheduledSessions: 'Scheduled sessions',
        thirtyMin: '30 min',
        totalAmount: 'Total Amount',
        totalSessions: 'Total Sessions:',
        remove: 'Remove',
      },
      preferences: {
        title: 'Preferences',
        required: 'Required',
        emailConfirmation: {
          label: 'Send email confirmation',
          description: 'Receive appointment details and reminders via email',
        },
        addToCalendar: {
          label: 'Add to calendar automatically',
          description: 'We\'ll send calendar invites for Google, Outlook, and Apple Calendar',
        },
        smsReminder: {
          label: 'Remind me by SMS',
          description: 'Get a text reminder 1 hour before your appointment',
        },
        wechatReminder: {
          label: 'Remind me by WeChat',
          description: 'Get a WeChat message reminder 1 hour before your appointment',
        },
      },
      payment: {
        title: 'Complete Your Payment',
        subtitle: 'Review your booking details and complete payment.',
        bookingSummary: 'Booking Summary',
        selectPaymentMethod: 'Select Payment Method',
        paymentMethodSelected: 'Payment method selected',
        methods: {
          paypal: {
            name: 'PayPal',
            description: 'Pay securely with PayPal',
            instruction: 'Click the button below to complete your payment via PayPal',
            button: 'Pay with PayPal',
          },
          venmo: {
            name: 'Venmo', 
            description: 'Send via Venmo app',
            instruction: 'Scan QR code with Venmo:',
            placeholder: '[Venmo QR Code]',
          },
          wechat: {
            name: 'WeChat Pay',
            description: 'Scan QR code to pay',
            instruction: 'Scan QR code with WeChat:',
            placeholder: '[WeChat Pay QR Code]',
          },
          alipay: {
            name: 'Alipay',
            description: 'Scan QR code to pay', 
            instruction: 'Scan QR code with Alipay:',
            placeholder: '[Alipay QR Code]',
          },
        },
        paymentNote: {
          title: 'Note:',
          message: 'Your booking is confirmed instantly after payment. You will receive a confirmation email with meeting details.',
        },
        confirmationNote: 'Your booking will be confirmed after payment is received',
      },
    },
    buttons: {
      continue: 'Continue to Time Selection →',
      confirm: 'Confirm & Schedule →',
      back: '← Back to details',
      returnHome: 'Return to Homepage',
      exploreServices: 'Explore Services →',
      continueToPayment: 'Continue to Payment →',
      finalizeBooking: 'Finalize Booking →',
      backToTimeSelection: '← Back to time selection',
      creatingAppointment: 'Creating appointment...',
    },
    confirmationNote: 'A confirmation email will be sent immediately.',
    errorMessages: {
      selectDateTime: 'Please select a date and time for your appointment.',
      submitError: 'An unexpected error occurred. Please try again.',
    },
    monthNames: {
      january: 'January',
      february: 'February', 
      march: 'March',
      april: 'April',
      may: 'May',
      june: 'June',
      july: 'July',
      august: 'August',
      september: 'September',
      october: 'October',
      november: 'November',
      december: 'December',
    },
    successModal: {
      title: 'Thank You - You\'re All Set!',
      zoom: {
        message: 'A confirmation email with your Zoom link is on the way to {email}. We look forward to meeting you online.',
        messageWithPayment: 'Your payment has been received, and your consultation is confirmed. A confirmation email with your Zoom link is on the way to {email}. We look forward to meeting you online.',
      },
      phone: {
        message: 'A confirmation has been sent. We\'ll call you at {phone} at your scheduled time. We look forward to speaking with you.',
        messageWithPayment: 'Your payment has been received, and your consultation is confirmed. A confirmation has been sent. We\'ll call you at {phone} at your scheduled time. We look forward to speaking with you.',
      },
      wechat: {
        message: 'We\'ll contact you on WeChat at {wechatId}. We look forward to connecting with you.',
        messageWithPayment: 'Your payment has been received, and your consultation is confirmed. We\'ll contact you on WeChat at {wechatId}. We look forward to connecting with you.',
      },
    },
    phoneCountries: {
      us: '+1 (US/CA)',
      cn: '+86 (CN)',
      hk: '+852 (HK)',
      mo: '+853 (MO)',
      tw: '+886 (TW)',
      sg: '+65 (SG)',
      uk: '+44 (UK)',
    },
    dayNames: {
      sun: 'Sun',
      mon: 'Mon',
      tue: 'Tue',
      wed: 'Wed',
      thu: 'Thu',
      fri: 'Fri',
      sat: 'Sat',
    },
  },

  // Common/Shared
  common: {
    loading: 'Loading...',
    error: 'An error occurred',
    retry: 'Try Again',
    close: 'Close',
    next: 'Next',
    previous: 'Previous',
    save: 'Save',
    cancel: 'Cancel',
  },
};

export type TranslationSchema = typeof en;
